import { boxes, tf } from './helpers.js';
/**
 * The widgets of Chubb's fillable PDF for revenue above RM1 billion (task 92, decision 0069), mapped
 * against the blank form and checked by filling every widget with its field's name. This edition has no
 * boxes for an intrusion prevention system or next-generation anti-malware, so those two are never ticked.
 * Fields only the broker or director fill in, the declarations and the regulations table are left blank.
 */
export const CHUBB_ERM_LARGE_PDF = {
    sha256: 'b9d6afe4ac2c066e64954401586302aa6ec811d51f2cf812d0984215ebb13e9e',
    pages: 22,
    fields: {
        company_name: tf(3),
        website: tf(5),
        headquarters: tf(8),
        year_established: tf(7),
        employees: tf(9),
        security_contact: { table: { 0: { name: tf(11), role: tf(13), email: tf(16), phone: tf(17) } } },
        turnover_prior: tf(20),
        turnover_current: tf(21),
        turnover_next: tf(23),
        turnover_online_pct: tf(35),
        activities: tf(121),
        subsidiary: boxes({ yes: 11, no: 4 }),
        subsidiary_detail: tf(116),
        sanctions: boxes({ yes: 13, no: 12 }),
        overseas: boxes({ yes: 15, no: 14 }),
        overseas_list: {
            table: {
                0: { entity: tf(66), country: tf(68), revenue_pct: tf(69) },
                1: { entity: tf(117), country: tf(118), revenue_pct: tf(119) },
            },
        },
        pii_records: tf(76),
        card_records: tf(82),
        health_records: tf(83),
        processor: boxes({ yes: 17, no: 16 }),
        processor_detail: tf(79),
        pci: boxes({ yes: 19, no: 18 }),
        pci_transactions: tf(81),
        pci_outsourced: boxes({ yes: 21, no: 20 }),
        pci_level: boxes({ level_1: 23, level_2: 24, level_3: 25, level_4: 26, not_compliant: 27 }),
        privacy_policy: boxes({ yes: 29, no: 28 }),
        security_policy: boxes({ yes: 31, no: 30 }),
        classification_policy: boxes({ yes: 33, no: 32 }),
        data_security_staff: boxes({ yes: 35, no: 34 }),
        it_security_staff: boxes({ yes: 37, no: 36 }),
        ir_plan: boxes({ yes: 39, no: 38 }),
        breach_plan: boxes({ yes: 42, no: 40 }),
        privacy_monitoring: boxes({ yes: 43, no: 41 }),
        group_central: boxes({ yes: 45, no: 44 }),
        group_baseline: boxes({ yes: 48, no: 46 }),
        group_audited: boxes({ yes: 49, no: 47 }),
        standards: tf(103),
        biometric: boxes({ yes: 106, no: 102 }),
        pii_encrypted_db_pct: tf(108),
        pii_in_transit: boxes({ yes: 107, no: 103 }),
        dlp: boxes({ yes: 1018, no: 1017 }),
        pii_largest_store: tf(1010),
        microsegmentation: boxes({ yes: 1025, no: 1024 }),
        pii_need_to_know: boxes({ yes: 1027, no: 1026 }),
        pii_minimisation: boxes({ anonymisation: 123, pseudonymisation: 133, tokenisation: 134, other: 135 }),
        processor_contracts: boxes({ yes: 128, no: 127 }),
        processor_reviewed: boxes({ yes: 130, no: 129 }),
        processor_breach_terms: boxes({ yes: 132, no: 131 }),
        hosted_centrally: boxes({ yes: 143, no: 142, partial: 138 }),
        flat_network: boxes({ yes: 141, no: 140 }),
        segmentation: boxes({
            vlan: 144,
            host_firewalls: 148,
            acl: 149,
            air_gap: 150,
            sdn: 151,
            least_privilege: 152,
            other: 153,
        }),
        external_pentest: boxes({ yes: 411, no: 410 }),
        internal_pentest: boxes({ yes: 167, no: 166 }),
        waf: boxes({ yes: 169, no: 168 }),
        mobile_access: boxes({ yes: 171, no: 170 }),
        mdm_byod_pct: tf(1017),
        remote_access: boxes({ yes: 182, no: 181 }),
        remote_methods: boxes({ vpn: 183, mfa: 187, sso_mfa: 184, ztna: 188, encryption: 189, other: 190 }),
        remote_pct_staff: tf(1022),
        remote_pct_contractors: tf(1021),
        remote_pct_vendors: tf(1019),
        remote_pct_privileged: tf(1020),
        rdp_use: boxes({ not_used: 195, internal_only: 198, remote_access: 196, other: 199 }),
        rdp_protections: boxes({ vpn: 201, mfa: 204, nla: 202, honeypots: 205, other: 203 }),
        iam_programme: boxes({ yes: 208, no: 207 }),
        domain_admins: tf(1026),
        wide_admins: tf(1027),
        admin_users: tf(1028),
        admin_justification: tf(1029),
        account_management: boxes({
            manual_review: 209,
            audit_tool: 2011,
            service_unprivileged: 2012,
            directory_monitored: 2010,
        }),
        local_admin_disabled: boxes({ yes: 1029, no: 1028 }),
        separate_admin_accounts: boxes({ yes: 226, no: 225 }),
        paw: boxes({ yes: 212, no: 211 }),
        logs_90_days: boxes({ yes: 214, no: 213 }),
        admin_tiering: boxes({ yes: 227, no: 216, na: 215 }),
        privileged_auto_logoff: boxes({ yes: 218, no: 217 }),
        pam: boxes({ yes: 220, no: 219 }),
        privileged_mfa: boxes({ yes: 222, no: 221 }),
        breakglass_no_mfa: tf(1033),
        breakglass_long_password: boxes({ yes: 230, no: 229, na: 228 }),
        mfa_independent: boxes({ yes: 233, no: 232, na: 231 }),
        webmail_mfa: boxes({ yes: 236, no: 235, na: 234 }),
        email_security: boxes({
            external_tag: 237,
            spf: 238,
            dkim: 243,
            gateway: 244,
            malware_scan: 245,
            quarantine: 246,
            secure_send: 249,
            macros: 248,
            other: 252,
            none: 250,
            sandboxing: 247,
            phishing_training: 251,
        }),
        bcp: boxes({ yes: 259, no: 258 }),
        bcp_tested: boxes({ yes: 256, no: 254, na: 253 }),
        drp: boxes({ yes: 264, no: 262 }),
        drp_tested: boxes({ yes: 263, no: 261, na: 260 }),
        backup_protections: boxes({
            immutable: 2013,
            offline: 2015,
            separate_account: 2014,
            mfa: 2016,
            encrypted: 2018,
            cloud_segmented: 2017,
            other: 265,
            none: 2019,
        }),
        full_restore_tested: boxes({ yes: 279, no: 278 }),
        recoverability_tested: boxes({ yes: 268, no: 266 }),
        ransomware_plan: boxes({ yes: 271, no: 270 }),
        backup_malware_scan: boxes({ yes: 274, no: 272 }),
        email_backups: boxes({ yes: 275, no: 273 }),
        critical_systems: {
            table: {
                0: {
                    system: tf(1040),
                    provider: tf(1041),
                    bia: tf(1043),
                    rpo_hours: tf(1044),
                    rto_hours: tf(1046),
                    backup_frequency: tf(1045),
                },
                1: {
                    system: tf(1042),
                    provider: tf(1048),
                    bia: tf(1049),
                    rpo_hours: tf(1050),
                    rto_hours: tf(1051),
                    backup_frequency: tf(1047),
                },
                2: {
                    system: tf(1052),
                    provider: tf(1054),
                    bia: tf(1055),
                    rpo_hours: tf(1056),
                    rto_hours: tf(1057),
                    backup_frequency: tf(1053),
                },
                3: {
                    system: tf(1058),
                    provider: tf(1060),
                    bia: tf(1061),
                    rpo_hours: tf(1062),
                    rto_hours: tf(1063),
                    backup_frequency: tf(1059),
                },
            },
        },
        alternative_systems: boxes({ yes: 285, no: 282 }),
        failover_kinds: boxes({
            active_active: 2020,
            active_passive: 2025,
            manual: 2022,
            offline_environment: 2023,
            alternative_provider: 2027,
            colocation: 2021,
            other: 2026,
        }),
        alternate_power: boxes({ yes: 291, no: 289 }),
        extra_bandwidth: boxes({ yes: 290, no: 288 }),
        generators_tested: boxes({ yes: 299, no: 298 }),
        secure_development: boxes({ yes: 301, no: 300 }),
        ddos: boxes({ yes: 305, no: 303 }),
        threat_intel: boxes({ yes: 306, no: 304 }),
        monitoring: boxes({
            ids: 2024,
            web_filtering: 2028,
            isolation: 2029,
            advanced_endpoint: 2032,
            edr: 2033,
            mdr: 2036,
            xdr: 2038,
            manual_log_review: 2035,
            soc: 2042,
            siem: 2039,
            soar: 2053,
            managed_firewall: 2051,
            protective_dns: 2056,
            other: 2054,
        }),
        edr_pct: tf(1065),
        edr_provider: tf(1066),
        edr_auto: boxes({ yes: 309, no: 307 }),
        edr_to_siem: boxes({ yes: 3010, no: 308 }),
        siem_pct: tf(1067),
        siem_provider: tf(1068),
        soc_type: boxes({ internal: 2043, external: 2045, hybrid: 2046, round_the_clock: 2047 }),
        inventory: boxes({ yes: 3013, no: 3011 }),
        inventory_pct: tf(1070),
        scan_scope_pct: tf(10116),
        cmdb: boxes({ yes: 3018, no: 3017, partial: 3015 }),
        asset_risk_levels: boxes({ yes: 3014, no: 3012 }),
        scan_internal: tf(1071),
        scan_external: tf(1072),
        scan_tools: tf(1073),
        eol_critical: boxes({ yes: 3020, no: 3016 }),
        eol_sensitive: boxes({ yes: 3021, no: 3019 }),
        eol_no_internet: boxes({ yes: 3024, no: 3022 }),
        eol_segregated: boxes({ yes: 3025, no: 3023 }),
        eol_detail: tf(1075),
        external_ports_scanned: boxes({ yes: 313, no: 311 }),
        ports_disabled: boxes({ yes: 319, no: 318 }),
        patch_process: boxes({ yes: 321, no: 320 }),
        patch_days_low: tf(1074),
        patch_days_medium: tf(1078),
        patch_days_high: tf(1079),
        patch_days_critical: tf(1080),
        patches_tested: boxes({ yes: 325, no: 324 }),
        vendor_team: boxes({ yes: 330, no: 328 }),
        vendor_assessed: boxes({ yes: 329, no: 327, partial: 326 }),
        vendor_risk: boxes({ yes: 333, no: 332, partial: 331 }),
        vendor_contingency: boxes({
            alternatives_available: 2058,
            some_contracted: 2063,
            single_source: 2066,
            identified_only: 2065,
        }),
        vendor_checks: boxes({
            infosec_cert: 2059,
            resilience_cert: 2064,
            pentest: 2067,
            rating_service: 2068,
            backup_review: 2069,
            sla: 2070,
            mfa_review: 2071,
            dpia: 2072,
            dpa: 2073,
            other: 2074,
        }),
        recourse_waived: boxes({ never: 2075, sometimes: 2078, mostly: 2081 }),
        uses_cloud: boxes({ yes: 338, no: 336 }),
        cloud_policy: boxes({ yes: 339, no: 337, na: 335 }),
        cloud_controls: boxes({
            casb: 2077,
            sase: 2080,
            ztna: 2082,
            sso: 2085,
            mfa_critical: 2086,
            mfa_other: 2087,
        }),
        acquisitions: tf(1088),
        acquisition_detail: tf(1090),
        acquisition_review: boxes({ before: 2079, before_integration: 2088, rarely: 2089, other: 2090 }),
        trademarks_screened: boxes({ yes: 341, no: 340 }),
        releases_obtained: boxes({ yes: 343, no: 342 }),
        legal_review: boxes({ yes: 345, no: 344 }),
        outsourced_content: boxes({ yes: 347, no: 346 }),
        indemnities: boxes({ yes: 349, no: 348 }),
        policies_reviewed: boxes({ yes: 351, no: 350 }),
        losses: boxes({
            data_breach: 2091,
            cyber_incident: 2092,
            system_failure: 2094,
            media_claim: 2093,
            regulatory_action: 2095,
            supplier_breach: 2098,
            supplier_incident: 2099,
        }),
        loss_detail: { columns: { what: tf(1093), impact: tf(1094), mitigation: tf(1095) } },
        circumstances: boxes({ yes: 370, no: 369 }),
        circumstances_detail: tf(1096),
        bio_employees: boxes({ yes: 372, no: 371 }),
        bio_contractors: boxes({ yes: 374, no: 373 }),
        bio_customers: boxes({ yes: 377, no: 375 }),
        bio_release: boxes({ yes: 381, no: 379 }),
        bio_arbitration: boxes({ yes: 382, no: 380 }),
        bio_policy: boxes({ yes: 384, no: 383 }),
        bio_explicit_consent: boxes({ yes: 386, no: 385 }),
        bio_records: tf(10100),
        ot_policy: boxes({ yes: 413, no: 412 }),
        ot_sites: tf(101020),
        ot_sites_segmented: boxes({ yes: 388, no: 387 }),
        ot_internet_segmented: boxes({ yes: 390, no: 389 }),
        ot_remote: boxes({ yes: 392, no: 391 }),
        ot_monitored: boxes({ yes: 394, no: 393 }),
        ot_backups: boxes({ yes: 396, no: 395 }),
        ot_make_up: boxes({ yes: 398, no: 397 }),
        pi_insurance: boxes({ yes: 4017, no: 4015 }),
        pi_cyber_exclusion: boxes({ yes: 4018, no: 4016 }),
        hosts_for_clients: boxes({ yes: 404, no: 403 }),
        client_responsible: boxes({ yes: 406, no: 405 }),
        controls_apply_hosted: boxes({ yes: 408, no: 407 }),
        pos_segregated: boxes({ yes: 4011, no: 409 }),
        emv_pct: tf(101017),
        payment_providers: tf(101016),
        pos_monitored: boxes({ yes: 4012, no: 4010 }),
        franchise: boxes({ yes: 4014, no: 4013 }),
        franchise_detail: tf(101019),
    },
};
//# sourceMappingURL=chubb-erm-large-pdf.js.map